import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import WorkStatusCard from './components/WorkStatusCard';
import TimeTrackingWidget from './components/TimeTrackingWidget';
import EarningsOverview from './components/EarningsOverview';
import QuickActionsPanel from './components/QuickActionsPanel';
import NotificationFeed from './components/NotificationFeed';
import { useAuth } from '../../contexts/AuthContext';
import jobService from '../../services/jobService';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

const ContractorDashboard = () => {
  const { userProfile } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (userProfile?.id) {
      loadAssignments();
    }
  }, [userProfile]);

  useEffect(() => {
    if (userProfile?.id) {
      const channel = supabase?.channel('contractor-jobs')?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'job_assignments',
            filter: `contractor_id=eq.${userProfile?.id}`
          },
          (payload) => {
            if (payload?.eventType === 'INSERT') {
              loadAssignments();
            } else if (payload?.eventType === 'UPDATE') {
              setAssignments(prev => prev?.map(a => 
                a?.id === payload?.new?.id ? { ...a, ...payload?.new } : a
              ));
            } else if (payload?.eventType === 'DELETE') {
              setAssignments(prev => prev?.filter(a => a?.id !== payload?.old?.id));
            }
          }
        )?.subscribe();

      return () => supabase?.removeChannel(channel);
    }
  }, [userProfile]);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      const data = await jobService?.getJobAssignments(userProfile?.id);
      setAssignments(data);
    } catch (err) {
      console.error('Error loading assignments:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const acceptedJobs = assignments?.filter(a => a?.job?.status === 'open');
  const ongoingJobs = assignments?.filter(a => a?.job?.status === 'ongoing');
  const completedJobs = assignments?.filter(a => a?.job?.status === 'completed');
  const allJobs = [...acceptedJobs, ...ongoingJobs, ...completedJobs];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Laden...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <p className="text-error">{error}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 md:space-y-8">
        <div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-2">
            Vakman Dashboard
          </h1>
          <p className="text-muted-foreground">
            Welkom terug, {userProfile?.full_name || 'Vakman'}! Hier is uw overzicht van projecten.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <WorkStatusCard
            title="Geaccepteerde Opdrachten"
            count={acceptedJobs?.length}
            jobs={acceptedJobs?.map(a => ({
              id: a?.id,
              title: a?.job?.title || 'Job',
              client: 'Client',
              location: a?.job?.location || 'Unknown',
              startDate: new Date(a.started_at)?.toLocaleDateString('nl-NL'),
              rate: parseFloat(a?.hourly_rate),
              rateType: 'hourly'
            }))}
            icon="CheckCircle"
            color="success"
            status="accepted"
          />
          <WorkStatusCard
            title="Lopende Opdrachten"
            count={ongoingJobs?.length}
            jobs={ongoingJobs?.map(a => ({
              id: a?.id,
              title: a?.job?.title || 'Job',
              client: 'Client',
              location: a?.job?.location || 'Unknown',
              startDate: new Date(a.started_at)?.toLocaleDateString('nl-NL'),
              rate: parseFloat(a?.hourly_rate),
              rateType: 'hourly',
              progress: a?.progress || 0,
              hoursWorked: parseFloat(a?.hours_worked || 0)
            }))}
            icon="Clock"
            color="warning"
            status="ongoing"
          />
          <WorkStatusCard
            title="Afgeronde Opdrachten"
            count={completedJobs?.length}
            jobs={completedJobs?.map(a => ({
              id: a?.id,
              title: a?.job?.title || 'Job',
              client: 'Client',
              location: a?.job?.location || 'Unknown',
              startDate: new Date(a.started_at)?.toLocaleDateString('nl-NL'),
              rate: parseFloat(a?.hourly_rate),
              rateType: 'hourly',
              hoursWorked: parseFloat(a?.hours_worked || 0)
            }))}
            icon="CheckCircle2"
            color="primary"
            status="completed"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TimeTrackingWidget jobs={allJobs} />
          <EarningsOverview />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <QuickActionsPanel />
          </div>
          <div>
            <NotificationFeed />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ContractorDashboard;